import React, { useEffect } from 'react';
import { Carousel, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { LoginOutlined } from '@ant-design/icons';
import { FaReact, FaNodeJs } from 'react-icons/fa';
import { DiGo, DiMongodb } from 'react-icons/di';
import { SiCplusplus, SiTailwindcss, SiAntdesign, SiQt, SiJsonwebtokens, SiCanva } from 'react-icons/si';
import { FaNetworkWired } from "react-icons/fa";
import { RiLockPasswordLine } from 'react-icons/ri';
import GoCode from '../assets/gocode.png';
import DriverUI from '../assets/driver-ui.png';
import Backend from '../assets/gocode.png';
import WebUI from '../assets/dashboard.jpeg';
import './Home.css';
import { isAuthenticated } from '../utils/jwt';

const { Title } = Typography;

const Home = () => {
  const navigate = useNavigate();

  const handleLoginClick = async () => {
    const auth = await isAuthenticated();
    if (auth) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  useEffect(() => {
    // Remove dashboard class from html and body
    document.documentElement.classList.remove('dashboard');
    document.body.classList.remove('dashboard');
  }, []);

  return (
    <div className="home-container">
      <div className="navbar fixed top-0 left-0 w-full bg-slate-300 text-pastelGreen flex justify-end p-4 rounded-b-lg z-50">
        <LoginOutlined className="text-white text-2xl cursor-pointer hover:text-slate-300" onClick={handleLoginClick} />
      </div>
      <div className="relative top-10">
        <Carousel autoplay className="carousel w-full h-96">
          <div>
            <img src={GoCode} alt="Image 1" className="w-full h-96 object-cover" />
          </div>
          <div>
            <img src={DriverUI} alt="Image 2" className="w-full h-96 object-cover" />
          </div>
        </Carousel>
        <div className="absolute inset-y-0 right-0 w-1/4 flex items-center justify-center text-center text-pastelGreen carousel-trapezoid">
          <div>
            <h1 className="text-3xl font-bold">MSU SRT Telemetry</h1>
            <h2 className="text-lg">Developed by the Software & Telemetry sub-team</h2>
          </div>
        </div>
      </div>
      <div className="projects-section mt-12">
        <div className="project-container">
          <div className="project-description-left">
            <div className="description-box">
              <Title level={4} className="project-title">Driver UI</Title>
              <p className="project-description">
                This project is an application written in C++ that serves the necessary information from our solar car to a display for our driver to view.
                Not only does it just serve data to a GUI, but it also handles and decodes all of the CAN dataframes. Along with serving data to the driver,
                It also integrates with our web UI via a secured websocket tunnel with JWT and serves data to the rest of our crew. It is extremely efficient
                as a result of being written with C++ using the Qt library, which has enabled for seamless multithreading, websocket integration, and GUI management.
                The car connects to the internet via a 4G LTE router. We are extremely proud of this project!
                This is the stack we use:            
              </p>
              <br/>
              <p className="project-description">
                <span className="icon-text"><SiCplusplus className="tech-icon" /> C++</span><br />
                <span className="icon-text"><SiQt className="tech-icon" /> Qt</span><br />
                <span className="icon-text"><FaNetworkWired className="tech-icon" /> Websockets</span><br />
                <span className="icon-text"><SiCanva className="tech-icon" /> CAN FD</span><br />
                <span className="icon-text"><SiJsonwebtokens className="tech-icon" /> JWT</span>
              </p>
            </div>
          </div>
          <div className="project-image-right">
            <img src={DriverUI} alt="Driver UI" className="project-image" />
          </div>
        </div>
        <div className="project-container">
          <div className="project-image-left">
            <img src={Backend} alt="Backend" className="project-image" />
          </div>
          <div className="project-description-right">
            <div className="description-box">
              <Title level={4} className="project-title">Backend</Title>
              <p className="project-description">
                This project primarily handles the connection between our driver UI and web UI. Our driver UI uses the backend to secure a websocket connection 
                from the car to the backend. Then the data from the car is received as a JSON forwarded to our web UI. On the web UI the backend handles user authentication 
                for our login system, which secures a websocket tunnel to the backend and then the web UI receives the JSON and manipulates the DOM. This project is the backbone 
                of the entire telemetry system.
                This is the stack we use:            
              </p>
              <br/>
              <p className="project-description">
                <span className="icon-text"><DiGo className="tech-icon" /> GoLang</span><br />
                <span className="icon-text"><DiMongodb className="tech-icon" /> MongoDB</span><br />
                <span className="icon-text"><SiJsonwebtokens className="tech-icon" /> JWT</span><br />
                <span className="icon-text"><FaNetworkWired className="tech-icon" /> Websockets</span><br />
                <span className="icon-text"><FaNodeJs className="tech-icon" /> API</span><br />
                <span className="icon-text"><RiLockPasswordLine className="tech-icon" /> Encryption</span>
              </p>
            </div>
          </div>
        </div>
        <div className="project-container">
          <div className="project-description-left">
            <div className="description-box">
              <Title level={4} className="project-title">Web UI</Title>
              <p className="project-description">
                This project is what you are viewing right now. We decided to make a nice little homepae to showcase our projects and give
                those without access to our dashboard something to view. This project integrates with our backend via an API for our login and websocket connection 
                once the websocket connection is established, the data from our car feeds the dahboard on the web UI. The dashboard contains an overview and in-depth page.
                The overview page shows data graphs, counters, and any generic data about the car. The in-depth page, shows all of the data the car can send. It also 
                shows a 3D wireframe model of our vehicle which displays the temperature of our vehicle.
                This is the stack we use:            
              </p>
              <br/>
              <p className="project-description">
                <span className="icon-text"><FaReact className="tech-icon" /> React</span><br />
                <span className="icon-text"><SiAntdesign className="tech-icon" /> Ant Design</span><br />
                <span className="icon-text"><SiTailwindcss className="tech-icon" /> Tailwind</span><br />
                <span className="icon-text"><SiJsonwebtokens className="tech-icon" /> JWT</span><br />
                <span className="icon-text"><FaNetworkWired className="tech-icon" /> Websockets</span>
              </p>
            </div>
          </div>
          <div className="project-image-right">
            <img src={WebUI} alt="Web UI" className="project-image" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
